import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Copy, Eye, EyeOff, Key, Trash2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import apiService from "@/lib/api-functions/api-service";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface ApiKeyData {
  id: string;
  company_id: string;
  api_key_masked?: string;
  api_key?: string;
  api_secret?: string;
  name: string;
  created_by: string;
  last_used_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function ApiKeysSection() {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedCompany } = useSimplifiedUserData();
  
  const [apiKeyData, setApiKeyData] = useState<ApiKeyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showSecretDialog, setShowSecretDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("API Key");
  const [generatedKey, setGeneratedKey] = useState<{ api_key: string; api_secret: string } | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  const fetchApiKey = useCallback(async () => {
    if (!selectedCompany?.id) return;

    setLoading(true);
    try {
      const response = await apiService.getCompanyApiKey(selectedCompany.id);

      if (response.success && response.data) {
        setApiKeyData(response.data);
      } else if (response.error?.includes('not found') || response.error?.includes('404')) {
        // No API key exists yet
        setApiKeyData(null);
      } else {
        throw new Error(response.error || 'Failed to fetch API key');
      }
    } catch (error) {
      console.error('Error fetching API key:', error);
      // Don't show error toast if key doesn't exist
      if (error instanceof Error && !error.message.includes('not found') && !error.message.includes('404')) {
        toast({
          title: "Error",
          description: error.message || "Failed to fetch API key",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.id, toast]);

  // Fetch API key data
  useEffect(() => {
    if (selectedCompany?.id) {
      fetchApiKey();
    }
  }, [selectedCompany, fetchApiKey]);

  const handleGenerateKey = async () => {
    if (!selectedCompany?.id) {
      toast({
        title: "Error",
        description: "No company selected",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.createCompanyApiKey(selectedCompany.id, newKeyName);

      if (response.success && response.data) {
        setGeneratedKey({
          api_key: response.data.api_key,
          api_secret: response.data.api_secret,
        });
        setShowGenerateDialog(false);
        setShowSecretDialog(true);
        setNewKeyName("API Key");
        
        // Refresh the key list
        await fetchApiKey();

        toast({
          title: "Success",
          description: "API key generated successfully",
        });
      } else {
        throw new Error(response.error || 'Failed to generate API key');
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate API key",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!selectedCompany?.id) return;

    setLoading(true);
    try {
      const response = await apiService.deleteCompanyApiKey(selectedCompany.id);

      if (response.success) {
        setApiKeyData(null);
        setShowRevokeDialog(false);
        
        toast({
          title: "Success",
          description: "API key revoked successfully",
        });
      } else {
        throw new Error(response.error || 'Failed to revoke API key');
      }
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to revoke API key",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: `${label} copied to clipboard`,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">{t('platform.settings.apikeyssection.api_keys')}</h2>
        <p className="text-muted-foreground">
          Manage API keys for programmatic access to your company's data. Use these keys with the VS Code plugin or custom integrations.
        </p>
      </div>

      {loading && !apiKeyData ? (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="animate-spin mr-2" size={20} />
              Loading...
            </div>
          </CardContent>
        </Card>
      ) : apiKeyData ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Key size={20} />
                  {apiKeyData.name}
                </CardTitle>
                <CardDescription>
                  Created on {formatDate(apiKeyData.created_at)}
                </CardDescription>
              </div>
              <Badge variant={apiKeyData.is_active ? "default" : "secondary"}>
                {apiKeyData.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">API Key</label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={apiKeyData.api_key_masked || ''}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(apiKeyData.api_key_masked || '', 'API key')}
                >
                  <Copy size={16} />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Use this key with X-API-Key header
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">{t('platform.settings.apikeyssection.last_used')}</label>
              <p className="text-sm text-muted-foreground mt-1">
                {formatDate(apiKeyData.last_used_at)}
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowGenerateDialog(true)}
                disabled={loading}
              >
                <RefreshCw size={16} className="mr-2" />
                Regenerate Key
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowRevokeDialog(true)}
                disabled={loading}
              >
                <Trash2 size={16} className="mr-2" />
                Revoke Key
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <Key size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">{t('platform.settings.apikeyssection.no_api_key')}</h3>
              <p className="text-muted-foreground mb-4">
                Generate an API key to enable programmatic access to your company's data.
              </p>
              <Button onClick={() => setShowGenerateDialog(true)} disabled={loading}>
                <Key size={16} className="mr-2" />
                Generate API Key
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documentation */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.settings.apikeyssection.using_api_keys')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">{t('platform.settings.apikeyssection.vs_code_plugin_setup')}</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              <li>{t('platform.settings.apikeyssection.generate_an_api_key_above')}</li>
              <li>{t('platform.settings.apikeyssection.copy_both_the_api_key_and_secret')}</li>
              <li>{t('platform.settings.apikeyssection.in_vs_code_go_to_settings_extensions_locanest')}</li>
              <li>{t('platform.settings.apikeyssection.paste_your_api_key_and_secret')}</li>
              <li>{t('platform.settings.apikeyssection.set_your_game_id')}</li>
              <li>{t('platform.settings.apikeyssection.disable_offline_mode')}</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium mb-2">{t('platform.settings.apikeyssection.making_api_requests')}</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Include these headers in your requests:
            </p>
            <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
{`X-API-Key: your-api-key
X-API-Secret: your-api-secret`}
            </pre>
          </div>

          <div>
            <h4 className="font-medium mb-2">{t('platform.settings.apikeyssection.security_best_practices')}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>{t('platform.settings.apikeyssection.never_commit_api_keys_to_version_control')}</li>
              <li>{t('platform.settings.apikeyssection.store_secrets_in_environment_variables')}</li>
              <li>{t('platform.settings.apikeyssection.rotate_keys_periodically')}</li>
              <li>{t('platform.settings.apikeyssection.revoke_keys_immediately_if_compromised')}</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Generate Dialog */}
      <AlertDialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {apiKeyData ? 'Regenerate API Key' : 'Generate API Key'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {apiKeyData
                ? 'This will deactivate your current API key and generate a new one. Any integrations using the old key will stop working.'
                : 'Generate a new API key for programmatic access to your company data.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t('platform.settings.apikeyssection.key_name')}</label>
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={t('platform.settings.apikeyssection.eg_vs_code_plugin')}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A friendly name to identify this key
              </p>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleGenerateKey} disabled={loading || !newKeyName.trim()}>
              {loading ? 'Generating...' : apiKeyData ? 'Regenerate' : 'Generate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Show Secret Dialog */}
      <AlertDialog open={showSecretDialog} onOpenChange={setShowSecretDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('platform.settings.apikeyssection.api_key_generated_successfully')}</AlertDialogTitle>
            <AlertDialogDescription>
              ⚠️ Important: This is the only time you'll see the API secret. Copy and store it securely now.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t('platform.settings.apikeyssection.api_key_label')}</label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={generatedKey?.api_key || ''}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(generatedKey?.api_key || '', 'API key')}
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('platform.settings.apikeyssection.api_secret')}</label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={showSecret ? (generatedKey?.api_secret || '') : '••••••••••••••••••••••••••••••••'}
                  readOnly
                  className="font-mono text-sm"
                  type={showSecret ? 'text' : 'password'}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowSecret(!showSecret)}
                >
                  {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(generatedKey?.api_secret || '', 'API secret')}
                >
                  <Copy size={16} />
                </Button>
              </div>
              <p className="text-xs text-destructive mt-1 font-medium">
                ⚠️ This secret will not be shown again. Store it securely.
              </p>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              setShowSecretDialog(false);
              setGeneratedKey(null);
              setShowSecret(false);
            }}>
              I've Saved the Secret
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Dialog */}
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('platform.settings.apikeyssection.revoke_api_key')}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this API key? Any integrations using this key will immediately stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowRevokeDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRevokeKey} 
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? 'Revoking...' : 'Revoke Key'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

